import { ButtonLink } from "../components/Button";
import { Callout } from "../components/Callout";
import { Card } from "../components/Card";
import { Container } from "../components/Container";
import { GoogleMark } from "../components/GoogleMark";
import { signInUrl } from "../session/signIn";
import type { SignedOutReason } from "../session/resolveSession";
import styles from "./screens.module.css";
import signIn from "./SignInScreen.module.css";

/**
 * The signed-out surface.
 *
 * One control, and nothing to type into. Canon decision `D-008` makes Google Sign-In the
 * only way a founder reaches this desk, so a password field, an email field or a box asking
 * for a link would each be a lie about how sign-in works — and the last of those is exactly
 * the habit the withdrawn management link taught. There is no second way in to offer.
 *
 * The control is an anchor, not a button, because sign-in is a **top-level navigation**
 * (`contracts/API.md` § Seller sign-in shape): the browser leaves for core, core sends it to
 * Google, and it comes back with the session cookie already set. Keeping it an anchor also
 * keeps middle-click, "copy link" and the browser's own history behaving normally.
 *
 * A refused sign-in renders one honest message above the same control, because the control
 * *is* the retry. Core merges every cause of a refusal into one redirect on purpose, so
 * there is one message; inventing a more specific one would mean guessing.
 */

export function SignInScreen({
  reason,
  search = typeof window === "undefined" ? "" : window.location.search,
}: {
  reason: SignedOutReason;
  /** Test seam: the real app reads its own address bar for the invite code. */
  search?: string | undefined;
}) {
  return (
    <Container as="main" id="main" className={styles.main}>
      <div className={styles.panel}>
        <h1 className={styles.title}>Open your seller desk</h1>
        <p className={styles.body}>
          Dealbid uses your Google account and nothing else. There is no password to set, no
          link to keep safe, and nothing to type in.
        </p>

        {reason === "sign-in-failed" ? (
          <Callout tone="warning" title="Signing in didn't finish">
            Dealbid couldn't complete that sign-in, so it stopped rather than guessing.
            Nothing was charged and nothing changed. Try again below.
          </Callout>
        ) : null}

        <div className={styles.actions}>
          <ButtonLink size="lg" href={signInUrl(search)} className={signIn.google}>
            <GoogleMark />
            Continue with Google
          </ButtonLink>
        </div>

        <Card tone="elevated" className={signIn.trust}>
          <h2 className={signIn.trustTitle}>What signing in does</h2>
          <ul className={signIn.trustList}>
            <li>Google confirms who you are. Dealbid never sees your Google password.</li>
            <li>
              Your desk opens with a session this page cannot read, copy or forward — there
              is no link that would let someone else in.
            </li>
            <li>
              Signing in is free. Your first deal still goes through review before it is
              listed.
            </li>
          </ul>
        </Card>

        <p className={styles.footnote}>
          New to Dealbid? Signing in is also how you start — it creates your seller account.
        </p>
      </div>
    </Container>
  );
}
